import * as React from 'react';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import Spinner from '@rio-cloud/rio-uikit/Spinner';
import { switchAccountThunk } from '../../thunks';
import { selectAccounts, selectPending, selectSwitchAccountFailed } from '../../appSlice';
import { renderSpinner } from '../../common';
import { useAppDispatch, useAppSelector } from '../../../../configuration/setup/hooks';
import { AccountsList } from './AccountList';
import { AccountSearch } from './AccountSearch';

export const Accounts: React.FC = () => {
    const accounts = useAppSelector(selectAccounts);
    const isPending = useAppSelector(selectPending);
    const switchAccountFailed = useAppSelector(selectSwitchAccountFailed);

    const { formatMessage } = useIntl();

    const dispatch = useAppDispatch();
    const [selectedAccountId, setSelectedAccountId] = useState('');
    const [searchValue, setSearchValue] = useState('');

    const isDisabled = () => {
        if (selectedAccountId === '') {
            return 'disabled';
        }
        return '';
    };

    const renderConfirmText = () => {
        const textNode = formatMessage({ id: 'intl-msg:settingsDialog.tab.accounts.button.switch.label' });
        return isPending ? <Spinner text={textNode} /> : textNode;
    };

    const renderInlineError = () => {
        return (
            <div className={'text-right text-color-danger margin-top-10'}>
                <span className={'rioglyph rioglyph-warning-sign margin-right-5 text-size-large'} />
                <FormattedMessage
                    id={'intl-msg:settingsDialog.tab.accounts.error.switchAccount'}
                    defaultMessage={'Could not switch account'}
                />
            </div>
        );
    };

    const renderSwitchButton = () => (
        <div className={'btn-toolbar pull-right margin-top-20 margin-bottom-20'}>
            <div
                className={`btn btn-primary ${isDisabled()}`}
                onClick={() => selectedAccountId && dispatch(switchAccountThunk(selectedAccountId))}
                data-cy={'account-switch-btn'}
            >
                {renderConfirmText()}
            </div>
        </div>
    );

    const renderContent = () => (
        <>
            <div className="text-color-dark">
                <FormattedMessage id={'intl-msg:settingsDialog.tab.accounts.text.current'} />
            </div>
            <div className="radio-text-wrapper bg-white border rounded padding-10">
                <div className="margin-left-5 margin-right-5 text-medium">
                    {accounts.find(({ active }) => active)?.name}
                </div>
            </div>

            <div className="text-color-dark margin-top-20">
                <FormattedMessage id={'intl-msg:settingsDialog.tab.accounts.button.switch.label'} />
            </div>
            {accounts.length > 1 ? (
                <div data-cy={'switch-account-content'}>
                    <AccountSearch
                        value={searchValue}
                        onChange={(value) => {
                            setSearchValue(value);
                        }}
                    />
                    <AccountsList
                        selectedAccountId={selectedAccountId}
                        onSelectionChange={(accountId) => {
                            setSelectedAccountId(accountId);
                        }}
                        accounts={accounts
                            .filter(({ active }) => !active)
                            .filter(({ name }) => name.toLowerCase().includes(searchValue.toLowerCase()))}
                    />
                    {switchAccountFailed ? renderInlineError() : <></>}
                    {renderSwitchButton()}
                </div>
            ) : (
                <div>
                    <FormattedMessage id={'intl-msg:settingsDialog.tab.accounts.text.noOtherAccounts'} />
                </div>
            )}
        </>
    );

    return <>{isPending ? renderSpinner() : renderContent()}</>;
};
